<template>
  <div class="erase-workspace">
    <div class="erase-layout">
      <aside class="erase-side erase-controls">
        <section class="side-section">
          <div class="section-title"><span>01</span><div><strong>空间场景</strong><small>JPG / PNG / WebP，最大 10MB</small></div></div>
          <label
            class="erase-upload"
            :class="{ filled: previewUrl, dragging }"
            @dragenter.prevent="dragging = true"
            @dragover.prevent="dragging = true"
            @dragleave.prevent="dragging = false"
            @drop.prevent="handleDrop"
          >
            <input type="file" accept="image/png,image/jpeg,image/webp" @change="handleFileInput" />
            <template v-if="previewUrl">
              <img :src="$ossImageUrl(previewUrl, 'preview')" alt="待处理室内图片" decoding="async" />
              <span class="replace-photo"><i class="el-icon-refresh"></i> 更换图片</span>
            </template>
            <template v-else>
              <span class="upload-symbol"><i class="el-icon-picture-outline"></i></span>
              <strong>上传室内图片</strong>
              <small>点击选择或拖到这里</small>
            </template>
          </label>
        </section>

        <section class="side-section erase-method">
          <div class="section-title"><span>02</span><div><strong>消除内容</strong><small>使用画笔沿目标外围圈选</small></div></div>
          <button class="method-card active" type="button">
            <i class="el-icon-edit"></i>
            <span><strong>画笔圈选</strong><small>沿目标外围画圈，橡皮擦修正线条</small></span>
            <b>未选区锁定</b>
          </button>
          <p class="selection-empty">上传图片后点击“画笔精准圈选”，沿着需要删除的目标外围画出闭合轮廓。</p>
        </section>

        <section class="side-section erase-note">
          <div class="erase-note-head">
            <label for="erase-note">消除提示词 <span>已预设，可继续补充</span></label>
            <button
              type="button"
              :class="{ loading: writingNote }"
              :disabled="writingNote || generating"
              @click="writeEraseNote"
            >
              <span v-if="writingNote" class="note-spinner"></span>
              <i v-else class="el-icon-magic-stick"></i>
              {{ writingNote ? 'AI 写作中' : 'AI 帮我写' }}
            </button>
          </div>
          <textarea ref="eraseNote" id="erase-note" v-model.trim="userNote" :readonly="writingNote" maxlength="1200" placeholder="可在默认提示词后继续补充要删除的内容或背景要求"></textarea>
          <small>{{ userNote.length }}/1200</small>
          <AiPromptOptimizer
            v-model="userNote"
            :disabled="writingNote || generating"
            :max-length="1200"
            context="智能消除图片中标记目标的补充说明；必须保留默认的局部编辑边界，不得改变任何标记范围外的内容。"
          />
        </section>

      </aside>

      <main class="erase-stage-panel">
        <div class="stage-toolbar">
          <div class="stage-heading">
            <span>03</span>
            <div><strong>编辑与预览</strong><small>使用画笔圈出目标并查看消除结果</small></div>
          </div>
          <div class="stage-toolbar-actions">
            <div class="toolbar-group">
              <button :class="{ active: viewMode === 'mark' }" :disabled="!previewUrl" title="画笔圈选" @click="openMaskedEditor"><i class="el-icon-edit"></i><span>画笔圈选</span></button>
              <button :class="{ active: viewMode === 'result' }" :disabled="!resultUrl" title="查看消除结果" @click="viewMode = 'result'"><i class="el-icon-view"></i><span>结果</span></button>
            </div>
          </div>
          <span v-if="previewUrl" class="image-meta">{{ sourceWidth }} × {{ sourceHeight }} · {{ selectedRatio }}</span>
        </div>

        <div class="canvas-shell" :class="{ drawing: isDrawing }">
          <div v-if="!previewUrl && !resultUrl" class="canvas-placeholder">
            <div class="canvas-empty-art">
              <span><i class="el-icon-picture-outline-round"></i></span>
              <i class="art-crop el-icon-crop"></i>
              <i class="art-spark">✦</i>
            </div>
            <strong>先上传一张室内图片</strong>
            <p>上传后可在这里拖动框选要消除的家具或物品</p>
            <em>请从左侧「空间场景」开始</em>
          </div>
          <template v-else-if="previewUrl && (viewMode === 'mark' || !resultUrl)">
            <canvas
              ref="eraseCanvas"
              aria-label="点击打开图片标记编辑器"
              @click="openMaskedEditor"
            ></canvas>
            <button type="button" class="canvas-hint mask-entry" @click="openMaskedEditor"><i class="el-icon-edit"></i> 用画笔圈出要消除的物体</button>
          </template>
          <div v-else class="result-stage">
            <img :src="$ossImageUrl(resultUrl, 'preview')" alt="智能消除结果" decoding="async" />
            <span class="result-badge"><i class="el-icon-check"></i> AI 消除完成</span>
          </div>
        </div>

        <div class="stage-footer">
          <div class="stage-tips">
            <span><i class="el-icon-circle-check"></i> 画笔标注目标</span>
            <span><i class="el-icon-circle-check"></i> 橡皮擦修正范围</span>
            <span><i class="el-icon-circle-check"></i> 支持撤销与重置</span>
          </div>
          <div v-if="resultUrl" class="stage-result-actions">
            <WorkflowTuningButton
              variant="button"
              :image-url="resultUrl"
              :prompt="finalPrompt"
              source-model="gpt-image-2-official"
              module-name="智能消除"
            />
            <button @click="downloadResult"><i class="el-icon-download"></i> 下载结果</button>
          </div>
        </div>

        <div v-if="errorMessage" class="erase-error">
          <i class="el-icon-warning-outline"></i>
          <div><strong>{{ errorMessage }}</strong><span>{{ errorSuggestion }}</span></div>
          <button v-if="currentTaskIds.length && errorRetryable" :disabled="generating" @click="continuePolling">继续查询</button>
        </div>
      </main>

    </div>

    <section class="erase-generation-bar">
      <div class="generation-intro">
        <span><i class="el-icon-magic-stick"></i></span>
        <div><strong>生成设置</strong><small>确认图片与框选区域后开始处理</small></div>
      </div>
      <div class="generation-status">
        <div class="generate-readiness">
          <span :class="{ ready: previewUrl }"><i :class="previewUrl ? 'el-icon-check' : 'el-icon-picture-outline'"></i> 原图已上传</span>
          <span :class="{ ready: previewUrl }"><i class="el-icon-lock"></i> 未标记区域保持原样</span>
          <span class="erase-model-badge"><i class="el-icon-cpu"></i> FastIMG · 局部修复</span>
        </div>
        <span class="erase-output-size"><i class="el-icon-full-screen"></i> {{ strictOutputDimensions }}</span>
        <QualitySelector v-model="selectedQuality" compact :disabled="generating" :show-label="false" />
        <div class="point-cost"><span>预计消耗</span><strong><i></i> {{ selectedQualityPoints }} 积分</strong></div>
      </div>
      <button class="erase-generate" :disabled="generating || !canOpenEditor" type="button" @click="openMaskedEditor">
        <span v-if="generating" class="erase-spinner"></span>
        <i v-else class="el-icon-magic-stick"></i>
        <span><strong>{{ generating ? generationText : '打开智能消除编辑器' }}</strong><small>{{ generating ? `${progress}%` : '标记目标、填写提示词并生成' }}</small></span>
      </button>
      <div v-if="generating" class="erase-progress"><i :style="{ width: `${progress}%` }"></i></div>
    </section>

    <section class="erase-history">
        <header>
          <div><span class="erase-kicker">GENERATION RECORDS</span><h2>智能消除记录</h2><small>每次生成都会自动保存在这里，可随时查看或管理</small></div>
          <button title="刷新记录" :disabled="historyLoading" @click="loadHistory"><i :class="historyLoading ? 'el-icon-loading' : 'el-icon-refresh'"></i></button>
        </header>
        <div v-if="!history.length" class="history-empty">
          <span><i :class="historyLoading ? 'el-icon-loading' : 'el-icon-time'"></i></span>
          <strong>{{ historyLoading ? '正在加载创作历史' : '暂无智能消除记录' }}</strong>
          <small>{{ historyLoading ? '正在获取历史记录，请稍候…' : '完成首次生成后，处理结果会自动出现在这里' }}</small>
        </div>
        <div v-else class="history-list">
          <article v-for="record in history" :key="record.id" :class="{ active: record.id === currentBatchId }">
            <div class="history-head">
              <span :class="record.status">{{ statusLabel(record.status) }}</span>
              <small><b>生成时间</b><time class="creation-date" :datetime="record.createdAt || undefined">{{ formatTime(record.createdAt) }}</time></small>
            </div>
            <div class="history-comparison">
              <button
                v-if="record.source"
                class="history-preview"
                title="查看消除前原图"
                @click="previewHistoryImage(record, 'source')"
              >
                <img :src="$ossImageUrl(record.source, 'thumbnail')" alt="消除前原图" loading="lazy" decoding="async" />
                <span class="history-image-label">消除前</span>
                <i class="el-icon-zoom-in"></i>
              </button>
              <div v-else class="history-preview history-placeholder">
                <span class="history-image-label">消除前</span>
                <i class="el-icon-picture-outline"></i>
                <small>原图暂不可用</small>
              </div>
              <button
                v-if="record.result"
                class="history-preview result"
                title="查看消除后效果"
                @click="previewHistoryImage(record, 'result')"
              >
                <img :src="$ossImageUrl(record.result, 'thumbnail')" alt="消除后效果图" loading="lazy" decoding="async" />
                <span class="history-image-label">消除后</span>
                <i class="el-icon-zoom-in"></i>
              </button>
              <div v-else class="history-preview history-placeholder result">
                <span class="history-image-label">消除后</span>
                <i :class="record.status === 'generating' ? 'el-icon-loading' : 'el-icon-warning-outline'"></i>
                <small>{{ record.status === 'generating' ? '正在生成' : '生成失败' }}</small>
              </div>
            </div>
            <ImageDimensionsRow :items="[{ label: '原图尺寸', url: record.source, probe: true }, { label: '结果尺寸', url: record.result, width: record.resultWidth, height: record.resultHeight }]" />
            <GenerationModelDetails :record="record" />
            <GenerationFailureDetails :record="record" />
            <div class="history-actions">
              <WorkflowTuningButton
                v-if="record.result"
                variant="button"
                :image-url="record.result"
                :prompt="record.prompt || finalPrompt"
                :source-model="record.model || ''"
                module-name="智能消除"
              />
              <button :disabled="!record.source && !record.result" @click="openHistory(record)"><i class="el-icon-view"></i> 查看对比</button>
              <button class="danger" @click="removeHistory(record)"><i class="el-icon-delete"></i></button>
            </div>
          </article>
        </div>
        <AppPagination :page="historyPage" :page-size="12" :total="historyTotal" :loading="historyLoading" @change="changeHistoryPage" />
    </section>
    <ReusableImageEditor
      :visible="maskedEdit.visible"
      :source-url="maskedEdit.sourceUrl"
      :submitting="maskedEdit.submitting"
      :error="maskedEdit.error"
      :initial-prompt="userNote"
      title="图片编辑"
      prompt-label="消除提示词"
      prompt-placeholder="默认规则已填写，可继续补充要删除的内容或背景要求"
      optimizer-context="智能消除：根据图片上的可视标记优化删除与背景补全提示词。必须保留用户原始意图和局部编辑边界，不得改变任何标记范围外的内容。"
      marker-guide="彩色笔迹只用于定位消除目标，不是画面内容。只有与笔迹直接重叠或被闭合笔迹包围的对象才是目标；最终必须去除全部笔迹。"
      execution-guard="【最高优先级】只允许处理彩色标记覆盖或闭合圈选范围内部。禁止按物体类别、外形相似性或排列关系搜索并删除其他对象；即使画面中存在多个相同或相似的对象，也只能删除标记位置的那一个，其他对象必须完整保留。范围外的任何像素和内容都不得删除、移动、替换或重绘。"
      @close="closeMaskedEditor"
      @save="generateErase"
    />
  </div>
</template>

<script>
/* 框选层只用于页面交互；生成只提交干净原图和精确坐标。 */
import { mergePendingGenerations, removePendingGeneration, savePendingGeneration } from '@/utils/pendingGenerationRecords'
import { apiRequest } from '@/services/api'
import { downloadImageAsPng } from '@/utils/imageDownload'
import ImageDimensionsRow from '@/components/ImageDimensionsRow.vue'
import AiPromptOptimizer from '@/components/AiPromptOptimizer.vue'
import QualitySelector from '@/components/QualitySelector.vue'
import ReusableImageEditor from '@/components/ReusableImageEditor.vue'
import { DEFAULT_GENERATION_QUALITY, MINIMUM_OUTPUT_RESOLUTION, generationPoints, outputDimensions } from '@/utils/outputQuality'
import { selectionListCoordinateText } from '@/utils/selectionGeometry'

const DEFAULT_SMART_ERASE_PROMPT = '只处理彩色标记覆盖或闭合圈选范围内部，完整删除其中的目标，包括与目标直接相关的阴影和倒影，使结果看起来像该内容从未出现过一样。根据周边画面自然补全被遮挡的背景。目标必须与彩色笔迹在位置上直接重叠或被闭合笔迹包围；不得因为其他对象与目标类别相同、外形相似或排列相关就一并删除。即使画面中存在多个相同或相似的对象，也只能删除标记位置的那一个，其他对象必须完整保留。不得处理圈选范围外的任何东西，范围外的像素、内容、结构、构图和光线必须保持原样；最终不得残留目标碎片、彩色笔迹、明显边缘或修补痕迹。'

export default {
  name: 'SmartErase',
  components: { ImageDimensionsRow, AiPromptOptimizer, QualitySelector, ReusableImageEditor },
  data() {
    return {
      sourceFile: null,
      previewUrl: '',
      sourceImage: null,
      sourceWidth: 0,
      sourceHeight: 0,
      selectedRatio: '16:9',
      selectedResolution: MINIMUM_OUTPUT_RESOLUTION,
      selectedQuality: DEFAULT_GENERATION_QUALITY,
      selections: [],
      draftSelection: null,
      drawingStart: null,
      drawingPointerId: null,
      isDrawing: false,
      dragging: false,
      userNote: DEFAULT_SMART_ERASE_PROMPT,
      writingNote: false,
      viewMode: 'mark',
      resultUrl: '',
      generating: false,
      generationText: '正在准备',
      progress: 0,
      apiConfigured: false,
      currentBatchId: '',
      currentTaskIds: [],
      history: [],
      historyLoading: false,
      historyPage: 1,
      historyTotal: 0,
      errorMessage: '',
      errorSuggestion: '',
      errorRetryable: false,
      requestControllers: [],
      pollingStopped: false,
      pendingRecordTimer: null,
      historyLoadErrorNotified: false,
      maskedEdit: { visible: false, sourceUrl: '', candidateUrl: '', width: 0, height: 0, submitting: false, error: '', iteration: 0 }
    }
  },
  computed: {
    strictOutputDimensions() {
      const dimensions = outputDimensions(this.selectedResolution)
      return `${dimensions.width} × ${dimensions.height}`
    },
    selectedQualityPoints() {
      return generationPoints(this.selectedQuality)
    },
    canOpenEditor() {
      return Boolean(this.sourceFile && this.sourceImage && this.apiConfigured)
    },
    selectionGeometryPrompt() {
      return selectionListCoordinateText(this.selections, '处理区域')
    },
    finalPrompt() {
      const note = this.userNote ? `用户补充要求：${this.userNote}。` : ''
      const targetInstruction = '本次目标是删除画笔标记区域内的内容。请完整移除标记覆盖的目标及其专属投影或反光，根据区域四周真实可见内容自然推断并无缝补全被遮挡部分。'
      return `执行室内图片局部智能消除。输入必须且只有一张不含框线、编号、标签或遮罩的干净原始室内照片，页面框线不会作为图片提交；只依据以下坐标定位：${this.selectionGeometryPrompt}。${targetInstruction}${note}最终画面不得生成矩形边界、颜色标记、遮罩、编号、文字或任何操作痕迹。必须采用局部修复方式：指定区域外像素直接继承原图，不得全图重绘；只允许修改指定区域内部及边缘融合所需的极窄过渡带。输出必须与原图逐像素对齐，原图中的消失点、墙顶地边界、门窗、吊顶分缝、柜体线条、家具轮廓和全部高对比边缘不得位移、缩放、弯曲或变形。严禁改变原图镜头、构图、画幅、房间结构、门窗、硬装、未选家具、材质、颜色和未选区域光线；不要重新设计空间，不要新增物体，不要输出拼图。`
    },
    historyViewerItems() {
      return this.history.reduce((items, record) => {
        if (record.source) {
          items.push({
            src: record.source,
            title: '智能消除 · 消除前',
            key: `${record.id}-source`
          })
        }
        if (record.result) {
          items.push({
            src: record.result,
            title: '智能消除 · 消除后',
            key: `${record.id}-result`
          })
        }
        return items
      }, [])
    }
  },
  mounted() {
    window.addEventListener('resize', this.drawCanvas)
    this.loadConfiguration()
    this.loadHistory()
  },
  beforeDestroy() {
    this.pollingStopped = true
    if (this.pendingRecordTimer) window.clearTimeout(this.pendingRecordTimer)
    window.removeEventListener('resize', this.drawCanvas)
    this.requestControllers.forEach(controller => controller.abort())
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl)
  },
  methods: {
    showMessage(message, type = 'info') {
      this.$message({ message, type, duration: 3200, showClose: true })
    },
    async request(url, options = {}) {
      return apiRequest(url, options, {
        controllers: this.requestControllers,
        isStopped: () => this.pollingStopped
      })
    },
    async loadConfiguration() {
      try {
        const health = await this.request('/api/health')
        this.apiConfigured = Boolean(health.configured)
      } catch (error) {
        this.apiConfigured = false
      }
    },
    handleFileInput(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (file) this.acceptFile(file)
    },
    handleDrop(event) {
      this.dragging = false
      const file = event.dataTransfer.files && event.dataTransfer.files[0]
      if (file) this.acceptFile(file)
    },
    acceptFile(file) {
      if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
        this.showMessage('仅支持 PNG、JPG 或 WebP 图片', 'warning')
        return
      }
      if (file.size >= 10 * 1024 * 1024) {
        this.showMessage('图片必须小于 10MB，请压缩后再上传', 'warning')
        return
      }
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl)
      const url = URL.createObjectURL(file)
      const image = new Image()
      image.onload = () => {
        this.sourceFile = file
        this.previewUrl = url
        this.sourceImage = image
        this.sourceWidth = image.naturalWidth
        this.sourceHeight = image.naturalHeight
        this.selectedRatio = '16:9'
        this.selections = []
        this.draftSelection = null
        this.resultUrl = ''
        this.viewMode = 'mark'
        this.errorMessage = ''
        this.$nextTick(this.drawCanvas)
      }
      image.onerror = () => {
        URL.revokeObjectURL(url)
        this.showMessage('无法读取图片，请重新选择', 'error')
      }
      image.src = url
    },
    drawCanvas() {
      const canvas = this.$refs.eraseCanvas
      if (!canvas || !this.sourceImage) return
      const maxSide = 2200
      const scale = Math.min(1, maxSide / Math.max(this.sourceWidth, this.sourceHeight))
      canvas.width = Math.max(1, Math.round(this.sourceWidth * scale))
      canvas.height = Math.max(1, Math.round(this.sourceHeight * scale))
      const context = canvas.getContext('2d')
      context.clearRect(0, 0, canvas.width, canvas.height)
      context.drawImage(this.sourceImage, 0, 0, canvas.width, canvas.height)
      const allSelections = this.draftSelection ? this.selections.concat(this.draftSelection) : this.selections
      allSelections.forEach((selection, index) => this.drawSelection(context, selection, index, canvas.width, canvas.height, selection === this.draftSelection))
    },
    drawSelection(context, selection, index, width, height, draft) {
      const x = selection.x * width
      const y = selection.y * height
      const w = selection.width * width
      const h = selection.height * height
      context.save()
      context.fillStyle = draft ? 'rgba(239,68,68,.18)' : 'rgba(239,68,68,.24)'
      context.strokeStyle = '#ef3123'
      context.lineWidth = Math.max(3, width / 650)
      context.setLineDash(draft ? [12, 8] : [])
      context.fillRect(x, y, w, h)
      context.strokeRect(x, y, w, h)
      context.setLineDash([])
      const radius = Math.max(14, Math.min(24, width / 38))
      context.fillStyle = '#ef3123'
      context.beginPath()
      context.arc(x + radius + 6, y + radius + 6, radius, 0, Math.PI * 2)
      context.fill()
      context.fillStyle = '#fff'
      context.font = `700 ${radius}px Arial`
      context.textAlign = 'center'
      context.textBaseline = 'middle'
      context.fillText(String(index + 1), x + radius + 6, y + radius + 7)
      context.restore()
    },
    canvasPoint(event) {
      const canvas = this.$refs.eraseCanvas
      const rect = canvas.getBoundingClientRect()
      return {
        x: Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)),
        y: Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height))
      }
    },
    startSelection(event) {
      if (!this.sourceImage || this.generating || this.viewMode !== 'mark') return
      if (event.pointerType === 'mouse' && event.button !== 0) return
      this.drawingStart = this.canvasPoint(event)
      this.drawingPointerId = event.pointerId
      this.isDrawing = true
      this.$refs.eraseCanvas.setPointerCapture(event.pointerId)
      event.preventDefault()
    },
    moveSelection(event) {
      if (!this.isDrawing || event.pointerId !== this.drawingPointerId) return
      const current = this.canvasPoint(event)
      this.draftSelection = {
        id: `draft-${Date.now()}`,
        x: Math.min(this.drawingStart.x, current.x),
        y: Math.min(this.drawingStart.y, current.y),
        width: Math.abs(current.x - this.drawingStart.x),
        height: Math.abs(current.y - this.drawingStart.y)
      }
      this.drawCanvas()
    },
    endSelection(event) {
      if (!this.isDrawing || event.pointerId !== this.drawingPointerId) return
      if (this.draftSelection && this.draftSelection.width >= 0.015 && this.draftSelection.height >= 0.015) {
        this.selections.push({ ...this.draftSelection, id: `selection-${Date.now()}-${this.selections.length}` })
        this.resultUrl = ''
      } else if (this.draftSelection) {
        this.showMessage('框选范围太小，请完整框住要消除的物体', 'warning')
      }
      this.draftSelection = null
      this.isDrawing = false
      this.drawingPointerId = null
      this.drawCanvas()
    },
    cancelSelection() {
      this.draftSelection = null
      this.isDrawing = false
      this.drawingPointerId = null
      this.drawCanvas()
    },
    undoSelection() {
      this.selections.pop()
      this.resultUrl = ''
      this.viewMode = 'mark'
      this.drawCanvas()
    },
    clearSelections() {
      this.selections = []
      this.resultUrl = ''
      this.viewMode = 'mark'
      this.drawCanvas()
    },
    removeSelection(index) {
      if (this.generating) return
      this.selections.splice(index, 1)
      this.resultUrl = ''
      this.viewMode = 'mark'
      this.drawCanvas()
    },
    async uploadFile(file) {
      const form = new FormData()
      form.append('file', file)
      return this.request('/api/images/upload', { method: 'POST', body: form, timeoutMs: 120000 })
    },
    async writeEraseNote() {
      if (this.writingNote || this.generating) return
      if (!this.sourceFile || !this.sourceImage) return this.showMessage('请先上传室内图片', 'warning')
      if (!this.apiConfigured) return this.showMessage('请先配置 ToAPIs API Key', 'warning')

      this.writingNote = true
      const previousNote = this.userNote
      try {
        const assets = await Promise.all([this.uploadFile(this.sourceFile)])
        const result = await this.request('/api/prompts/write-smart-erase-note', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageUrls: assets.map(asset => asset.url).filter(Boolean),
            floorPlanImageUrls: [],
            layoutNote: previousNote.trim(),
            viewpoint: '处理类型：删除用户稍后在统一编辑弹窗中用彩色画笔标记的内容并补全背景；具体范围由提交时的可视标记确定',
            space: '室内空间',
            style: '智能消除'
          }),
          timeoutMs: 120000,
          timeoutMessage: 'AI 写作等待超时，请稍后重试'
        })
        const writtenNote = String(result.prompt || '').trim()
        if (!writtenNote) throw new Error('AI 未返回可用的补充说明')
        this.userNote = writtenNote.slice(0, 1200)
        this.showMessage('AI 已根据原图和框选区域完成撰写', 'success')
      } catch (error) {
        this.userNote = previousNote
        this.showMessage(error.message || 'AI 写作失败，请稍后重试', 'error')
      } finally {
        this.writingNote = false
      }
    },
    createRequestId() {
      const random = window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`
      return `smart-erase-${random}`.slice(0, 64)
    },
    openMaskedEditor() {
      if (!this.previewUrl || !this.sourceImage || this.generating) return
      this.maskedEdit = {
        visible: true,
        sourceUrl: this.resultUrl || this.previewUrl,
        candidateUrl: '',
        width: this.sourceWidth,
        height: this.sourceHeight,
        submitting: false,
        error: '',
        iteration: 0
      }
    },
    closeMaskedEditor() {
      if (this.maskedEdit.submitting) return
      this.maskedEdit = { visible: false, sourceUrl: '', candidateUrl: '', width: 0, height: 0, submitting: false, error: '', iteration: 0 }
    },
    async generateErase(maskPayload) {
      if (this.generating) return
      if (!this.sourceFile) return this.showMessage('请先上传室内图片', 'warning')
      if (!maskPayload || !maskPayload.blob || !maskPayload.prompt) return this.showMessage('请先用画笔标记需要消除的内容', 'warning')
      if (!this.apiConfigured) return this.showMessage('请先配置 ToAPIs API Key', 'warning')
      this.generating = true
      this.maskedEdit.submitting = true
      this.maskedEdit.error = ''
      this.errorMessage = ''
      this.errorSuggestion = ''
      this.errorRetryable = false
      if (maskPayload.rawPrompt) this.userNote = maskPayload.rawPrompt
      const executionPrompt = maskPayload.prompt
      this.progress = 4
      this.generationText = '正在准备标记图片'
      const requestId = this.createRequestId()
      const pendingId = `interior-${requestId}`
      const pendingRecord = {
        id: pendingId,
        status: 'generating',
        createdAt: new Date().toISOString(),
        taskIds: [],
        source: this.previewUrl,
        result: ''
      }
      savePendingGeneration({
        id: pendingId,
        space: '室内空间',
        style: '智能消除',
        size: this.selectedRatio,
        prompt: executionPrompt,
        status: 'generating',
        stage: 'smart_erase',
        progress: 0,
        expectedCount: 1,
        tasks: [],
        images: [],
        inputContext: { floorPlanImages: [], referenceImages: [], annotation: null },
        createdAt: pendingRecord.createdAt
      })
      this.history = [pendingRecord, ...this.history.filter(item => item.id !== pendingRecord.id)].slice(0, 8)
      let generationRequestStarted = false
      try {
        this.progress = 8
        this.generationText = '正在上传标记图片'
        const cleanAsset = maskPayload.cleanBlob
          ? await this.uploadFile(new File([maskPayload.cleanBlob], `smart-erase-source-${Date.now()}.png`, { type: 'image/png' }))
          : maskPayload.sourceUrl && maskPayload.sourceUrl !== this.previewUrl
          ? { url: maskPayload.sourceUrl, localUrl: maskPayload.sourceUrl }
          : await this.uploadFile(this.sourceFile)
        const markedAsset = await this.uploadFile(new File([maskPayload.blob], `smart-erase-marked-${Date.now()}.png`, { type: 'image/png' }))
        const assets = [markedAsset]
        this.progress = 16
        this.generationText = '正在创建消除任务'
        pendingRecord.source = cleanAsset.localUrl || this.previewUrl
        savePendingGeneration({
          id: pendingId,
          space: '室内空间',
          style: '智能消除',
          size: this.selectedRatio,
          prompt: executionPrompt,
          status: 'generating',
          stage: 'smart_erase',
          progress: 0,
          expectedCount: 1,
          tasks: [],
          images: [],
          inputContext: {
            floorPlanImages: [],
            referenceImages: [cleanAsset].map(asset => ({
              type: 'reference',
              label: '原始图片',
              url: asset.localUrl
            })).filter(image => image.url),
            annotation: null
          },
          createdAt: pendingRecord.createdAt
        })
        generationRequestStarted = true
        const batch = await this.request('/api/generations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageUrls: assets.map(asset => asset.url),
            prompt: executionPrompt,
            space: '室内空间',
            style: '智能消除',
            size: this.selectedRatio,
            resolution: this.selectedResolution,
            quality: this.selectedQuality,
            count: 1,
            stage: 'smart_erase',
            floorPlanRecordUrls: [],
            referenceRecordUrls: [cleanAsset.localUrl].filter(Boolean),
            requestId
          }),
          timeoutMs: 120000,
          retries: 1,
          timeoutMessage: '创建消除任务等待超时，正在确认是否已经提交'
        })
        const taskIds = (batch.tasks || []).map(task => task.id).filter(Boolean)
        if (taskIds.length !== 1) {
          const pendingError = new Error('任务已保存，正在后台确认任务编号')
          pendingError.pending = true
          pendingError.retryable = true
          throw pendingError
        }
        this.currentBatchId = batch.batchId || ''
        this.currentTaskIds = taskIds
        pendingRecord.id = batch.batchId || pendingId
        pendingRecord.taskIds = taskIds
        removePendingGeneration(pendingId)
        this.progress = 20
        this.generationText = 'AI 正在移除并补全背景'
        const images = await this.pollTasks(taskIds)
        const completedUrl = images[0] || ''
        if (!completedUrl) throw new Error('智能消除未返回可用图片')
        this.resultUrl = completedUrl
        this.sourceWidth = Number(maskPayload.width || this.sourceWidth || 0)
        this.sourceHeight = Number(maskPayload.height || this.sourceHeight || 0)
        this.viewMode = 'result'
        this.progress = 100
        this.generationText = '智能消除已完成'
        this.maskedEdit.submitting = false
        this.closeMaskedEditor()
        this.showMessage('智能消除已完成并替换当前结果', 'success')
        await this.loadHistory()
      } catch (error) {
        if (pendingRecord) {
          if (generationRequestStarted && (error.retryable || error.pending)) {
            pendingRecord.status = 'generating'
            this.schedulePendingRecordRefresh()
          } else if (!pendingRecord.taskIds.length) {
            pendingRecord.status = 'failed'
            removePendingGeneration(pendingRecord.id)
          }
        }
        this.errorMessage = error.message || '智能消除失败'
        this.errorSuggestion = error.suggestion || '请检查框选范围和网络后重试。'
        this.errorRetryable = Boolean(error.pending || error.retryable)
        this.maskedEdit.error = this.errorMessage
        this.showMessage(this.errorMessage, error.pending ? 'warning' : 'error')
      } finally {
        this.generating = false
        this.maskedEdit.submitting = false
      }
    },
    async pollTasks(taskIds) {
      const startedAt = Date.now()
      let queryFailures = 0
      while (Date.now() - startedAt < 600000 && !this.pollingStopped) {
        try {
          const states = await Promise.all(taskIds.map(id => this.request(`/api/generations/${encodeURIComponent(id)}`, { timeoutMs: 12000, retries: 1 })))
          queryFailures = 0
          const images = states.map(item => item.status === 'completed' && item.result && item.result.data && item.result.data[0] ? item.result.data[0].url : '')
          const average = states.reduce((sum, item) => sum + (item.status === 'completed' ? 100 : Number(item.progress || 0)), 0) / states.length
          this.progress = Math.max(20, Math.min(99, Math.round(20 + average * 0.79)))
          this.generationText = `AI 正在移除并补全背景 ${Math.round(average)}%`
          if (states.every(item => ['completed', 'failed'].includes(item.status))) {
            const failed = states.find(item => item.status === 'failed')
            if (failed) {
              const error = new Error(failed.error && failed.error.message ? failed.error.message : '智能消除任务失败')
              Object.assign(error, failed.error || {})
              error.terminal = true
              throw error
            }
            if (images.some(image => !image)) {
              const error = new Error('任务已完成，但 AI 服务未返回可用图片')
              error.code = 'EMPTY_GENERATION_RESULT'
              error.suggestion = '任务记录已保留，请联系管理员并提供任务编号。'
              error.retryable = false
              error.terminal = true
              throw error
            }
            return images
          }
        } catch (error) {
          if (error.terminal || error.retryable === false) throw error
          queryFailures += 1
          if (queryFailures >= 5) {
            const pending = new Error('暂时无法查询进度，但任务可能仍在后台处理')
            pending.pending = true
            pending.retryable = true
            pending.suggestion = '请检查网络后点击“继续查询”，不需要重复提交。'
            throw pending
          }
        }
        await new Promise(resolve => window.setTimeout(resolve, 3000))
      }
      const pending = new Error('生成时间较长，任务仍在后台处理中')
      pending.pending = true
      pending.retryable = true
      pending.suggestion = '请稍后点击“继续查询”。'
      throw pending
    },
    async continuePolling() {
      if (this.generating || !this.currentTaskIds.length) return
      this.generating = true
      this.errorMessage = ''
      this.errorSuggestion = ''
      this.errorRetryable = false
      try {
        const images = await this.pollTasks(this.currentTaskIds)
        this.resultUrl = images[0] || ''
        this.viewMode = 'result'
        this.progress = 100
        await this.loadHistory()
      } catch (error) {
        this.errorMessage = error.message || '查询任务失败'
        this.errorSuggestion = error.suggestion || '请稍后重试。'
        this.errorRetryable = Boolean(error.pending || error.retryable)
      } finally {
        this.generating = false
      }
    },
    async loadHistory() {
      if (this.historyLoading) return
      this.historyLoading = true
      try {
        const response = await this.request(`/api/records/paged?stage=smart_erase&page=${this.historyPage}&pageSize=12`)
        this.historyTotal = Number(response.total || 0)
        const records = response.items || []
        const merged = this.historyPage === 1 ? mergePendingGenerations(records, 'smart_erase') : records
        this.history = merged.filter(record => record.stage === 'smart_erase').slice(0, 12).map(record => {
          const referenceImages = record.inputContext && record.inputContext.referenceImages ? record.inputContext.referenceImages : []
          return {
            id: record.id,
            status: record.status,
            createdAt: record.createdAt,
            taskIds: (record.tasks || []).map(task => task.id).filter(Boolean),
            taskStates: record.tasks || [],
            errorMessage: record.error || record.errorMessage || '',
            imageModel: record.imageModel || '',
            source: referenceImages[0] ? referenceImages[0].url : '',
            result: record.images && record.images[0] ? (record.images[0].url || record.images[0]) : ''
            , resultWidth: record.images && record.images[0] ? record.images[0].width : null
            , resultHeight: record.images && record.images[0] ? record.images[0].height : null
          }
        })
        const active = this.history.find(record => record.status === 'generating' && record.taskIds.length)
        if (active && !this.generating && !this.currentTaskIds.length) {
          this.currentBatchId = active.id
          this.currentTaskIds = active.taskIds
          this.continuePolling()
        }
        this.historyLoadErrorNotified = false
        this.schedulePendingRecordRefresh()
      } catch (error) {
        if (!this.history.length) this.history = []
        if (!this.historyLoadErrorNotified) {
          this.historyLoadErrorNotified = true
          this.showMessage(`${error.message || '暂时无法加载消除记录'}。${error.suggestion || '智能消除仍可继续使用，请稍后点击刷新记录。'}`, 'warning')
        }
      } finally {
        this.historyLoading = false
      }
    },
    schedulePendingRecordRefresh() {
      if (this.pendingRecordTimer) window.clearTimeout(this.pendingRecordTimer)
      if (this.pollingStopped || !this.history.some(record => record.status === 'generating' && !record.taskIds.length)) return
      this.pendingRecordTimer = window.setTimeout(() => {
        this.pendingRecordTimer = null
        this.loadHistory()
      }, 2000)
    },
    changeHistoryPage(page) {
      this.historyPage = page
      this.loadHistory()
    },
    openHistory(record) {
      if (!record.source && !record.result) return
      this.currentBatchId = record.id
      this.previewHistoryImage(record, record.source ? 'source' : 'result')
    },
    historyViewerIndex(recordId, type) {
      return this.historyViewerItems.findIndex(item => item.key === `${recordId}-${type}`)
    },
    previewHistoryImage(record, type) {
      const image = type === 'result' ? record.result : record.source
      if (!image) return
      this.currentBatchId = record.id
      this.$emit('preview-image', image, {
        title: type === 'result' ? '智能消除 · 消除后' : '智能消除 · 消除前',
        items: this.historyViewerItems,
        index: this.historyViewerIndex(record.id, type)
      })
    },
    async removeHistory(record) {
      try {
        await this.$confirm('删除这条智能消除记录后将无法恢复，是否继续？', '删除处理记录', { type: 'warning' })
      } catch (error) { return }
      try {
        await this.request(`/api/records/${encodeURIComponent(record.id)}`, { method: 'DELETE' })
        this.history = this.history.filter(item => item.id !== record.id)
        this.showMessage('记录已删除', 'success')
      } catch (error) {
        this.showMessage(error.message || '删除失败', 'error')
      }
    },
    statusLabel(status) {
      return { completed: '已完成', generating: '处理中', failed: '失败' }[status] || '处理中'
    },
    formatTime(value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
    },
    async downloadResult() {
      if (!this.resultUrl) return
      try {
        await downloadImageAsPng(this.resultUrl, `smart-erase-${Date.now()}`)
        this.showMessage('PNG 图片已开始下载', 'success')
      } catch (error) {
        this.showMessage(error.message || '下载失败，请稍后重试', 'error')
      }
    }
  }
}
</script>

<style scoped>
.erase-workspace { width: calc(100% - 68px); max-width: none; margin: 26px auto 64px; color: #243029; }
.erase-hero { min-height: 94px; display: flex; align-items: flex-start; justify-content: space-between; gap: 28px; margin-bottom: 18px; }
.erase-kicker { color: #2563eb; font-size: 9px; font-weight: 800; letter-spacing: 1.8px; }
.erase-hero h2 { margin: 7px 0 6px; font-size: 22px; letter-spacing: -.5px; }
.erase-hero p { max-width: 760px; margin: 0; color: #7a857e; font-size: 11px; line-height: 1.7; }
.privacy-chip { display: inline-flex; align-items: center; gap: 7px; border: 1px solid #dce5df; border-radius: 5px; padding: 8px 11px; background: #fff; color: #728078; font-size: 9px; font-weight: 700; white-space: nowrap; }
.privacy-chip i { color: #2d8a52; }
.erase-layout { display: grid; grid-template-columns: minmax(360px,.82fr) minmax(520px,1.18fr); gap: 16px; }
.erase-side, .erase-stage-panel { min-width: 0; overflow: hidden; border: 1px solid #e2e7e3; border-radius: 8px; background: #fff; box-shadow: 0 10px 28px rgba(37,51,43,.04); }
.erase-controls { display: flex; flex-direction: column; padding: 22px; }
.side-section + .side-section { margin-top: 22px; padding-top: 20px; border-top: 1px solid #edf0ee; }
.section-title { display: flex; align-items: center; gap: 11px; margin-bottom: 14px; }
.section-title > span { width: 29px; height: 29px; display: grid; place-items: center; flex: 0 0 29px; border-radius: 5px; background: #edf4ff; color: #2563eb; font-size: 10px; font-weight: 800; }
.section-title > div { display: flex; flex-direction: column; gap: 3px; }
.section-title strong { font-size: 13px; }
.section-title small { color: #8b948e; font-size: 9px; }
.erase-upload { height: 210px; display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 7px; position: relative; overflow: hidden; border: 1px dashed #bdc8c0; border-radius: 6px; background: linear-gradient(145deg,#fbfcfb,#f7faf8); cursor: pointer; transition: border .18s ease, background .18s ease; }
.erase-upload:hover,.erase-upload.dragging { border-color: #2563eb; background: #f6f9ff; }
.erase-upload input { position: absolute; width: 1px; height: 1px; opacity: 0; }
.upload-symbol { width: 48px; height: 48px; display: grid; place-items: center; margin-bottom: 3px; border-radius: 50%; background: #edf4ff; color: #2563eb; font-size: 22px; }
.erase-upload strong { color: #455149; font-size: 12px; }
.erase-upload small { color: #929c96; font-size: 9px; }
.erase-upload.filled img { width: 100%; height: 100%; display: block; object-fit: contain; background: #f2f4f2; }
.replace-photo { position: absolute; right: 7px; bottom: 7px; border-radius: 4px; padding: 5px 7px; background: rgba(22,29,25,.78); color: #fff; font-size: 8px; }
.method-card { width: 100%; min-height: 60px; display: flex; align-items: center; gap: 10px; border: 1px solid #9ab7ed; border-radius: 6px; padding: 11px 12px; background: #f7f9fd; color: #355077; text-align: left; box-shadow: 0 0 0 1px rgba(37,99,235,.04); }
.method-card > i { width: 34px; height: 34px; display: grid; place-items: center; border-radius: 5px; background: #2563eb; color: #fff; font-size: 16px; }
.method-card span { min-width: 0; display: flex; flex: 1; flex-direction: column; gap: 2px; }
.method-card strong { color: #2c3b33; font-size: 11px; }
.method-card small { color: #8491a3; font-size: 9px; }
.method-card b { color: #2563eb; font-size: 9px; white-space: nowrap; }
.selection-list { display: flex; flex-direction: column; gap: 5px; margin-top: 8px; }
.selection-list button { display: flex; align-items: center; gap: 7px; border: 1px solid #e1e5e2; border-radius: 4px; padding: 6px 7px; background: #fff; color: #68736d; font-size: 8px; }
.selection-list button:hover { border-color: #ef8f88; color: #d73528; }
.selection-list i { width: 16px; height: 16px; display: grid; place-items: center; border-radius: 50%; background: #ef3123; color: #fff; font-style: normal; font-weight: 700; }
.selection-list span { flex: 1; text-align: left; }
.selection-empty { margin: 9px 2px 0; color: #8e9892; font-size: 9px; line-height: 1.6; }
.erase-note { position: relative; }
.erase-note-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 8px; }
.erase-note label { display: flex; align-items: center; gap: 5px; margin-bottom: 8px; font-size: 11px; font-weight: 700; }
.erase-note-head label { margin-bottom: 0; }
.erase-note label span { color: #9aa39d; font-size: 9px; font-weight: 500; }
.erase-note-head button { min-height: 34px; display: inline-flex; align-items: center; gap: 6px; flex: 0 0 auto; border: 0; border-radius: 6px; padding: 0 13px; background: linear-gradient(135deg,#3478f6,#2458dc); color: #fff; font-size: 10px; font-weight: 800; box-shadow: 0 7px 16px rgba(37,99,235,.22); transition: background .18s ease, box-shadow .18s ease, transform .18s ease; }
.erase-note-head button:hover:not(:disabled) { background: linear-gradient(135deg,#2563eb,#1d4ed8); box-shadow: 0 9px 20px rgba(37,99,235,.3); transform: translateY(-1px); }
.erase-note-head button:disabled { opacity: .62; cursor: wait; }
.note-spinner { width: 13px; height: 13px; border: 2px solid rgba(255,255,255,.35); border-top-color: #fff; border-radius: 50%; animation: erase-spin .8s linear infinite; }
.erase-note textarea { width: 100%; height: 78px; resize: none; border: 1px solid #dce2de; border-radius: 5px; padding: 10px 11px; outline: none; color: #435048; font-size: 10px; line-height: 1.6; }
.erase-note textarea:focus { border-color: #6d96e9; box-shadow: 0 0 0 3px rgba(37,99,235,.08); }
.erase-note textarea[readonly] { background: #f7f9fb; color: #718078; }
.erase-note > small { position: absolute; right: 7px; bottom: 48px; color: #a2aaa5; font-size: 7px; }
.generate-area { margin-top: auto; padding-top: 26px; }
.point-cost { display: flex; align-items: center; justify-content: space-between; color: #8b948e; font-size: 9px; }
.point-cost strong { color: #59645e; }
.point-cost i { width: 9px; height: 9px; display: inline-block; border-radius: 50%; background: #f6a344; box-shadow: inset 0 0 0 2px #ffd8a3; vertical-align: -1px; }
.erase-generate { width: 100%; min-height: 54px; display: flex; align-items: center; justify-content: center; gap: 10px; border: 0; border-radius: 6px; padding: 9px 14px; background: #2563eb; color: #fff; box-shadow: 0 9px 20px rgba(37,99,235,.2); transition: background .18s ease, transform .18s ease; }
.erase-generate:hover:not(:disabled) { background: #1d4ed8; transform: translateY(-1px); }
.erase-generate:disabled { opacity: .52; cursor: not-allowed; box-shadow: none; }
.erase-generate > i { font-size: 18px; }
.erase-generate > span:not(.erase-spinner) { display: flex; align-items: flex-start; flex-direction: column; gap: 2px; }
.erase-generate strong { font-size: 12px; }
.erase-generate small { color: rgba(255,255,255,.72); font-size: 8px; }
.erase-spinner { width: 17px; height: 17px; border: 2px solid rgba(255,255,255,.32); border-top-color: #fff; border-radius: 50%; animation: erase-spin .8s linear infinite; }
.erase-progress { height: 3px; overflow: hidden; border-radius: 3px; background: #e5e9e6; }
.erase-progress i { height: 100%; display: block; background: #2563eb; transition: width .3s ease; }
.erase-generation-bar { position: relative; display: grid; grid-template-columns: auto minmax(0,1fr) 230px; align-items: center; gap: 22px; margin: 16px 0 28px; border: 1px solid #dce3de; border-radius: 8px; padding: 14px 16px; background: #fff; box-shadow: 0 9px 24px rgba(37,51,43,.05); }
.generation-intro { min-width: 170px; display: flex; align-items: center; gap: 10px; padding-right: 20px; border-right: 1px solid #e7ebe8; }
.generation-intro > span { width: 34px; height: 34px; flex: 0 0 34px; display: grid; place-items: center; border-radius: 6px; background: #edf4ff; color: #2563eb; font-size: 15px; }
.generation-intro > div { display: flex; flex-direction: column; gap: 3px; }
.generation-intro strong { color: #2f3c34; font-size: 11px; }
.generation-intro small { color: #909992; font-size: 8px; white-space: nowrap; }
.generation-status { min-width: 0; display: flex; align-items: center; justify-content: space-between; gap: 22px; }
.generate-readiness { display: flex; align-items: center; gap: 8px; }
.generate-readiness span { display: inline-flex; align-items: center; gap: 5px; border: 1px solid #e0e5e1; border-radius: 5px; padding: 8px 10px; background: #f7f9f7; color: #8a948e; font-size: 9px; white-space: nowrap; }
.generate-readiness span.ready { border-color: #b8d8c1; background: #f3faf5; color: #287548; }
.generate-readiness span.erase-model-badge { border-color: #c8d7f5; background: #f4f7ff; color: #315fae; }
.generate-readiness i { font-size: 11px; }
.erase-output-size { display: inline-flex; align-items: center; gap: 5px; border: 1px solid #c8d7f5; border-radius: 5px; padding: 8px 10px; background: #f4f7ff; color: #315fae; font-size: 9px; font-weight: 700; white-space: nowrap; }
.generation-status .point-cost { min-width: 94px; flex-direction: column; align-items: flex-start; gap: 4px; padding-left: 20px; border-left: 1px solid #e7ebe8; }
.generation-status .point-cost strong { font-size: 11px; white-space: nowrap; }
.erase-generation-bar .erase-generate { min-width: 230px; height: 54px; }
.erase-generation-bar .erase-progress { position: absolute; left: 0; right: 0; bottom: -1px; border-radius: 0 0 8px 8px; }
.erase-stage-panel { display: flex; flex-direction: column; background: #f5f6f5; }
.stage-toolbar { min-height: 70px; display: flex; align-items: center; gap: 12px; border-bottom: 1px solid #e1e5e2; padding: 11px 14px; background: #fff; }
.stage-heading { min-width: 0; display: flex; align-items: center; gap: 9px; }
.stage-heading > span { width: 29px; height: 29px; display: grid; place-items: center; flex: 0 0 29px; border-radius: 5px; background: #edf4ff; color: #2563eb; font-size: 10px; font-weight: 800; }
.stage-heading > div { min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.stage-heading strong { color: #2f3c34; font-size: 12px; white-space: nowrap; }
.stage-heading small { overflow: hidden; color: #8b948e; font-size: 8px; white-space: nowrap; text-overflow: ellipsis; }
.stage-toolbar-actions { display: flex; align-items: center; gap: 6px; margin-left: auto; }
.toolbar-group { display: flex; align-items: center; gap: 3px; border: 1px solid #e0e4e1; border-radius: 5px; padding: 3px; background: #f7f8f7; }
.toolbar-group button { height: 30px; display: inline-flex; align-items: center; gap: 5px; border: 0; border-radius: 3px; padding: 0 9px; background: transparent; color: #77827b; font-size: 9px; }
.toolbar-group button.active { background: #2563eb; color: #fff; box-shadow: 0 3px 7px rgba(37,99,235,.2); }
.toolbar-group button:disabled { opacity: .35; cursor: not-allowed; }
.toolbar-group.compact button { width: 31px; justify-content: center; padding: 0; }
.image-meta { flex: 0 0 auto; color: #929b95; font-size: 8px; }
.canvas-shell { min-height: 0; flex: 1; display: flex; align-items: center; justify-content: center; position: relative; overflow: hidden; padding: 18px; background-color: #f4f6f4; background-image: radial-gradient(#dfe4e0 .8px,transparent .8px); background-size: 15px 15px; }
.canvas-shell canvas { max-width: 100%; max-height: 560px; display: block; background: #fff; box-shadow: 0 12px 30px rgba(35,45,39,.16); cursor: crosshair; touch-action: none; user-select: none; }
.editor-draft-preview { max-width: 100%; max-height: 560px; display: block; object-fit: contain; background: #fff; box-shadow: 0 12px 30px rgba(35,45,39,.16); cursor: pointer; user-select: none; }
.canvas-shell.drawing canvas { cursor: crosshair; }
.canvas-placeholder { display: flex; align-items: center; flex-direction: column; text-align: center; }
.canvas-empty-art { width: 112px; height: 86px; position: relative; margin-bottom: 18px; }
.canvas-empty-art > span { width: 84px; height: 68px; position: absolute; left: 4px; bottom: 0; display: grid; place-items: center; border: 1px solid #d6dfd9; border-radius: 10px; background: #fff; color: #8f9b94; font-size: 27px; box-shadow: 0 12px 30px rgba(45,58,50,.09); }
.canvas-empty-art .art-crop { width: 34px; height: 34px; position: absolute; right: 2px; top: 0; display: grid; place-items: center; border: 3px solid #f4f6f4; border-radius: 8px; background: #2563eb; color: #fff; font-size: 15px; box-shadow: 0 7px 16px rgba(37,99,235,.2); }
.canvas-empty-art .art-spark { position: absolute; top: 6px; left: 1px; color: #6e91dc; font-size: 14px; font-style: normal; }
.canvas-placeholder strong { font-size: 13px; }
.canvas-placeholder p { margin: 6px 0 0; color: #87928b; font-size: 9px; }
.canvas-placeholder > em { margin-top: 13px; border-radius: 999px; padding: 6px 10px; background: #edf4ff; color: #4f72b8; font-size: 8px; font-style: normal; }
.canvas-hint { position: absolute; bottom: 30px; left: 50%; transform: translateX(-50%); border-radius: 999px; padding: 8px 12px; background: rgba(27,34,30,.82); color: #fff; font-size: 8px; pointer-events: none; white-space: nowrap; }
.result-stage { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; position: relative; }
.result-stage img { max-width: 100%; max-height: 560px; display: block; object-fit: contain; box-shadow: 0 12px 30px rgba(35,45,39,.16); }
.result-badge { position: absolute; top: 12px; right: 12px; display: inline-flex; align-items: center; gap: 5px; border-radius: 999px; padding: 7px 10px; background: rgba(28,119,67,.9); color: #fff; font-size: 8px; }
.stage-footer { min-height: 46px; display: flex; align-items: center; justify-content: space-between; gap: 12px; border-top: 1px solid #e0e5e1; padding: 9px 14px; background: #fff; color: #7e8882; font-size: 9px; }
.stage-tips { display: flex; align-items: center; gap: 14px; }
.stage-tips span { display: inline-flex; align-items: center; gap: 5px; color: #7e8882; white-space: nowrap; }
.stage-tips i { color: #5d83cf; }
.stage-result-actions { display: flex; align-items: center; gap: 7px; }
.stage-footer button { display: inline-flex; align-items: center; gap: 5px; border: 1px solid #b8c9eb; border-radius: 4px; padding: 6px 9px; background: #f5f8ff; color: #2459bb; font-size: 8px; }
.erase-error { display: flex; align-items: center; gap: 9px; border-top: 1px solid #f0c4c1; padding: 10px 14px; background: #fff2f1; color: #b43228; }
.erase-error > div { min-width: 0; display: flex; flex: 1; flex-direction: column; gap: 2px; }
.erase-error strong { font-size: 9px; }
.erase-error span { color: #8e5b57; font-size: 8px; }
.erase-error button { border: 1px solid #d68d86; border-radius: 4px; padding: 5px 8px; background: #fff; color: #b43228; font-size: 8px; }
.erase-history { margin-top: 32px; border-top: 1px solid #dfe5e0; padding-top: 26px; }
.erase-history > header { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; margin-bottom: 16px; }
.erase-history header > div { display: flex; flex-direction: column; align-items: flex-start; }
.erase-history header h2 { margin: 7px 0 4px; color: #202b25; font-size: 20px; letter-spacing: -.4px; }
.erase-history header small { color: #7a857e; font-size: 10px; }
.erase-history header button { width: 34px; height: 34px; border: 1px solid #dce2de; border-radius: 5px; background: #fff; color: #78837c; transition: .18s; }
.erase-history header button:hover { border-color: #9ab5e9; color: #2563eb; }
.history-empty { min-height: 190px; display: flex; align-items: center; justify-content: center; flex-direction: column; border: 1px dashed #ced8d1; border-radius: 8px; padding: 40px 16px; background: rgba(255,255,255,.62); text-align: center; }
.history-empty > span { width: 52px; height: 52px; display: grid; place-items: center; border-radius: 50%; background: #edf4ff; color: #6d8dce; font-size: 20px; box-shadow: inset 0 0 0 1px #dce7fa; }
.history-empty strong { margin-top: 12px; color: #536159; font-size: 11px; }
.history-empty small { margin-top: 5px; color: #929c96; font-size: 9px; }
.history-list { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 16px; }
.history-list article { min-width: 0; border: 1px solid #dce3de; border-radius: 8px; padding: 10px; background: #fff; box-shadow: 0 10px 28px rgba(37,51,43,.06); transition: border-color .18s ease, box-shadow .18s ease, transform .18s ease; }
.history-list article:hover { border-color: #c8d3cb; box-shadow: 0 16px 36px rgba(37,51,43,.1); transform: translateY(-2px); }
.history-list article.active { border-color: #789be3; box-shadow: 0 0 0 2px rgba(37,99,235,.07); }
.history-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 7px; }
.history-head span { border-radius: 3px; padding: 4px 6px; background: #edf0ee; color: #758079; font-size: 8px; }
.history-head span.completed { background: #e8f7ed; color: #25834c; }
.history-head span.failed { background: #fff0ef; color: #c33a30; }
.history-head small { min-width: 0; display: inline-flex; align-items: baseline; justify-content: flex-end; gap: 6px; color: #7e8982; font-size: 8px; }
.history-head small b { color: #9aa39e; font-weight: 700; }
.history-head small time { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.history-comparison { height: 230px; display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 1px; overflow: hidden; border: 1px solid #dfe5e1; border-radius: 6px; background: #dfe5e1; }
.history-preview { min-width: 0; height: 100%; display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 7px; position: relative; overflow: hidden; border: 0; border-radius: 0; padding: 0; background: #eef0ee; color: #7e8982; cursor: zoom-in; }
.history-preview img { width: 100%; height: 100%; display: block; object-fit: contain; object-position: center; background: #eef0ee; transition: filter .18s ease, transform .18s ease; }
.history-preview.result img { background: #edf3f1; }
.history-preview > .history-image-label { position: absolute; z-index: 2; left: 9px; top: 9px; border-radius: 4px; padding: 5px 7px; background: rgba(26,35,30,.78); color: #fff; font-size: 8px; font-weight: 700; line-height: 1; backdrop-filter: blur(5px); }
.history-preview.result > .history-image-label { background: rgba(37,99,235,.88); }
.history-preview > i:not(.el-icon-loading):not(.el-icon-warning-outline) { width: 30px; height: 30px; position: absolute; z-index: 2; right: 9px; bottom: 9px; display: grid; place-items: center; border-radius: 50%; background: rgba(20,29,24,.74); color: #fff; opacity: 0; transform: translateY(4px); transition: .18s ease; }
.history-preview:hover img { filter: brightness(.92); transform: scale(1.02); }
.history-preview:hover > i:not(.el-icon-loading):not(.el-icon-warning-outline),.history-preview:focus-visible > i:not(.el-icon-loading):not(.el-icon-warning-outline) { opacity: 1; transform: translateY(0); }
.history-preview:focus-visible { z-index: 1; outline: 3px solid #2563eb; outline-offset: -3px; }
.history-placeholder { cursor: default; }
.history-placeholder > i { color: #8a9690; font-size: 22px; }
.history-placeholder > i.el-icon-loading { color: #2563eb; }
.history-placeholder > i.el-icon-warning-outline { color: #c46a62; }
.history-placeholder > small { color: #8b958f; font-size: 9px; }
.history-actions { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 7px; }
.history-actions .workflow-tuning-button { flex: 1; width: auto; }
.history-actions button { min-height: 32px; display: inline-flex; flex: 1; align-items: center; justify-content: center; gap: 4px; border: 1px solid #e0e4e1; border-radius: 4px; background: #f8f9f8; color: #6f7a73; font-size: 9px; }
.history-actions button.danger { max-width: 34px; color: #c24136; }
@keyframes erase-spin { to { transform: rotate(360deg); } }
@media (max-width: 1180px) { .erase-layout { grid-template-columns: 1fr; }.erase-stage-panel { min-height: 640px; }.erase-generation-bar { grid-template-columns: auto 1fr; }.erase-generation-bar .erase-generate { grid-column: 1 / 3; width: 100%; }.generation-status { justify-content: flex-end; }.history-list { grid-template-columns: repeat(2,minmax(0,1fr)); } }
@media (max-width: 820px) { .erase-workspace { width: calc(100% - 26px); margin-top: 18px; }.erase-hero { align-items: flex-start; flex-direction: column; }.erase-stage-panel { min-height: 520px; }.canvas-shell { min-height: 410px; }.erase-generation-bar { display: flex; align-items: stretch; flex-direction: column; gap: 14px; }.generation-intro { min-width: 0; border-right: 0; border-bottom: 1px solid #e7ebe8; padding: 0 0 12px; }.generation-status { width: 100%; align-items: flex-start; flex-direction: column; gap: 12px; }.generate-readiness { width: 100%; flex-wrap: wrap; }.generate-readiness span { max-width: 100%; box-sizing: border-box; }.generation-status .point-cost { width: 100%; box-sizing: border-box; flex-direction: row; align-items: center; justify-content: space-between; border-left: 0; border-top: 1px solid #e7ebe8; padding: 12px 0 0; }.erase-generation-bar .erase-generate { min-width: 0; width: 100%; }.history-list { grid-template-columns: repeat(2,1fr); }.stage-toolbar { align-items: flex-start; flex-wrap: wrap; }.stage-toolbar-actions { order: 3; width: 100%; margin-left: 0; }.stage-tips { flex-wrap: wrap; gap: 7px 12px; }.image-meta { display: none; } }
@media (max-width: 520px) { .history-list { grid-template-columns: 1fr; } }
</style>
